import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const glow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(64, 224, 255, 0.3);
    opacity: 0.8;
  }
  50% { 
    box-shadow: 0 0 40px rgba(64, 224, 255, 0.6);
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(150deg, rgba(5, 10, 24, 1) 0%, rgba(10, 18, 36, 1) 50%, rgba(4, 8, 18, 1) 100%);
  color: #ffffff;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const BackgroundOrb = styled.div<{ $size: number; $top: string; $left: string; $delay?: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  top: ${props => props.$top};
  left: ${props => props.$left};
  background: radial-gradient(circle, rgba(64, 224, 255, 0.25) 0%, rgba(64, 224, 255, 0) 70%);
  filter: blur(45px);
  opacity: 0.6;
  pointer-events: none;
  animation: ${float} 9s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
`;

const Content = styled.div`
  text-align: center;
  z-index: 2;
  position: relative;
  max-width: 600px;
  width: 100%;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0 3rem;
  animation: ${float} 3s ease-in-out infinite;

  @media (max-width: 480px) {
    margin: 1.5rem 0 2rem;
  }
`;

const Icon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(64, 224, 255, 0.2), rgba(64, 224, 255, 0.1));
  border: 2px solid rgba(64, 224, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #40e0ff;
  font-size: 3rem;
  box-shadow: 0 8px 24px rgba(64, 224, 255, 0.2);

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
    font-size: 2.5rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 300;
  margin: 0 0 1.5rem 0;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: clamp(2rem, 7vw, 2.5rem);
    margin-bottom: 1rem;
  }
`;

const Message = styled.p`
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
  margin: 0 0 3rem 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1rem 2.5rem;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(64, 224, 255, 0.3), rgba(64, 224, 255, 0.16));
  border: 1px solid rgba(64, 224, 255, 0.45);
  color: #ffffff;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 42px rgba(64, 224, 255, 0.28);
    background: linear-gradient(135deg, rgba(64, 224, 255, 0.4), rgba(64, 224, 255, 0.25));
  }

  @media (max-width: 480px) {
    padding: 0.9rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set document title
    document.title = 'Page Not Found | Ollie Gillyon';
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <Wrapper>
      <BackgroundOrb $size={420} $top="-8%" $left="15%" />
      <BackgroundOrb $size={360} $top="65%" $left="75%" $delay={1.5} />
      <BackgroundOrb $size={280} $top="80%" $left="10%" $delay={0.8} />
      <Content>
        <IconContainer>
          <Icon>
            <FaSearch />
          </Icon>
        </IconContainer>
        <Title>This page doesn't exist</Title>
        <Message>
          We couldn't find what you're looking for. The page may have been moved or removed, or the link might be incorrect.
        </Message>
        <BackButton onClick={handleGoBack}>
          <FaArrowLeft />
          Go Back
        </BackButton>
      </Content>
    </Wrapper>
  );
};

export default NotFoundPage;

